'use client';

import HasprCursor, { CURSOR_HIDE, CURSOR_SHOW } from 'haspr-cursor'; // Import Wrapper
import { ThemeProvider, theme } from '@/components/features/theme/theme';
import { useEffect, useRef } from 'react';

import CookieConsent from '@/components/common/ui/CookieConsent';
import ErrorBoundary from '@/components/meta/ErrorBoundary';
import Footer from '@/components/layout/footer/Footer';
import GlobalStyle from '@/styles/global/globalStyle';
import Lenis from '@studio-freight/lenis';
import MenuComponent from '@/components/layout/menu/Menu';
import Preloader from '@/components/layout/preloader/Preloader';
import { ScrollSmoother } from 'gsap/dist/ScrollSmoother';
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { ShowSettingProvider } from '@/core/context/ShowSettingProvider';
import StickyButton from '@/components/common/StickyButton';
import StyledComponentsRegistry from '@/core/lib/registry';
import { Toaster } from 'sonner';
import getMenuData from '@/public/json/menu.json';
import getSettingsData from '@/public/json/settings.json';
import { gsap } from 'gsap';
import useAppEffects from '@/components/hooks/useAppEffects';
import { useFadeUpAnimation } from '@/components/animations/useFadeUpAnimation';
import useParallaxRight from '@/components/animations/useParallaxRight';
import { usePathname } from 'next/navigation';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);

export default function ClientLayout({ children }) {
	const pathName = usePathname();
	const isInitialLoadRef = useRef(true);
	const isFieldMountCategory = pathName?.startsWith('/product/category/');
	const isFieldMountStories = pathName?.startsWith('/impact-stories/');
	const pathname = usePathname();
	const lenisRef = useRef(null);

	const rafRef = useRef(null);
	const scrollHandlerRef = useRef(null);
	const gsapTickerRef = useRef(null);

	useEffect(() => {
		if (!lenisRef.current) {
			// Detect if device is mobile for performance optimization
			const isMobile = /iPhone|iPad|iPod|Android/i.test(
				navigator.userAgent,
			);

			// Obys-like smooth scroll configuration
			lenisRef.current = new Lenis({
				duration: isMobile ? 1.8 : 1.5, // Longer duration for buttery smoothness
				easing: t => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)), // Perfect easing curve like Obys
				direction: 'vertical',
				smooth: !isMobile,
				touchMultiplier: isMobile ? 1.5 : 1.8, // Smoother touch response
				wheelMultiplier: isMobile ? 0.6 : 0.8, // Reduced for ultra smooth feel
				lerp: isMobile ? 0.08 : 0.06, // Lower lerp for more smoothness
				infinite: false,
				orientation: 'vertical',
				gestureOrientation: 'vertical',
				wrapper: window,
				content: document.body,
				syncTouch: !isMobile,
				syncTouchLerp: 0.08, // Smoother touch lerp
				touchInertiaMultiplier: 25, // Reduced for smoother momentum
				preventScrollOnTouch: false, // Allow touch scrolling for better UX
				smoothmobile: false, // Disable smooth on mobile for performance
				normalizeWheel: true, // Normalize wheel delta for consistent experience
				nested: true, // Enable nested scroll for scrollable containers
			});

			// Optimized scroll handler with throttling
			let ticking = false;
			scrollHandlerRef.current = ({ scroll }) => {
				if (!ticking) {
					requestAnimationFrame(() => {
						const menuElement =
							document.querySelector('.site-menu');
						if (menuElement) {
							if (scroll > 300) {
								menuElement.classList.add('color');
							} else if (scroll < 300) {
								menuElement.classList.remove('color');
							}
						}
						// Throttled ScrollTrigger update
						ScrollTrigger.update();
						ticking = false;
					});
					ticking = true;
				}
			};
			lenisRef.current.on('scroll', scrollHandlerRef.current);

			// Optimized GSAP ticker integration
			gsapTickerRef.current = time => {
				if (lenisRef.current) {
					lenisRef.current.raf(time * 1000);
				}
			};
			gsap.ticker.add(gsapTickerRef.current);
			gsap.ticker.lagSmoothing(0);

			// Optimize performance settings
			gsap.config({
				force3D: true,
				nullTargetWarn: false,
				trialWarn: false,
			});

			// Make Lenis and ScrollTrigger globally available for page transitions
			window.lenisInstance = lenisRef.current;
			window.ScrollTrigger = ScrollTrigger;
		}

		return () => {
			// Cancel animation frame
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}

			// Remove GSAP ticker
			if (gsapTickerRef.current) {
				gsap.ticker.remove(gsapTickerRef.current);
				gsapTickerRef.current = null;
			}

			// Remove scroll event listener
			if (lenisRef.current && scrollHandlerRef.current) {
				lenisRef.current.off('scroll', scrollHandlerRef.current);
				scrollHandlerRef.current = null;
			}

			// Destroy Lenis instance
			if (lenisRef.current) {
				lenisRef.current.destroy();
				lenisRef.current = null;
			}

			// Kill all ScrollTrigger instances
			ScrollTrigger.getAll().forEach(trigger => trigger.kill());
		};
	}, [pathname]);

	// Handle the route change to scroll to the correct section
	useEffect(() => {
		const hash = window.location.hash;
		if (hash) {
			setTimeout(() => {
				lenisRef.current?.scrollTo(hash, { offset: -100 });
			}, 100);
		}
	}, []);

	// Handle smooth scrolling for hash links
	useEffect(() => {
		const handleHashLinkClick = e => {
			const target = e.target.closest('a[href^="#"]');
			if (!target) return;

			const hash = target.getAttribute('href');
			if (!hash || hash === '#') return;

			e.preventDefault();

			if (lenisRef.current) {
				lenisRef.current.scrollTo(hash, {
					offset: -100,
					duration: 1.2,
					easing: t => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
				});

				window.history.pushState(null, null, hash);
			}
		};

		document.addEventListener('click', handleHashLinkClick);

		return () => {
			document.removeEventListener('click', handleHashLinkClick);
		};
	}, []);

	useEffect(() => {
		setTimeout(() => {
			lenisRef.current?.resize();
		}, 500);
	}, []);

	// Handle initial load and route changes
	useEffect(() => {
		if (isInitialLoadRef.current) {
			// Show preloader only on initial load
			// Prevent scrolling during preloader
			if (typeof document !== 'undefined') {
				document.body.style.overflow = 'hidden';
				document.documentElement.style.overflow = 'hidden';
			}
		} else {
			// For route changes, just ensure smooth scrolling
			if (typeof document !== 'undefined') {
				document.body.style.overflow = '';
				document.documentElement.style.overflow = '';
			}
		}
	}, [pathName]);

	useEffect(() => {
		gsap.to(window, { duration: 0, scrollTo: 0 });
		// Scroll update all pages
		gsap.to('.site-menu', {
			opacity: 1,
			duration: 1, // Adjust duration as needed
			ease: 'power1.out', // Adjust ease as needed
		});
	}, [pathName]);

	useEffect(() => {
		document.body.classList.toggle(
			'color',
			Boolean(isFieldMountCategory || isFieldMountStories),
		);

		return () => {
			document.body.classList.remove('color');
		};
	}, [isFieldMountCategory, isFieldMountStories]);

	useFadeUpAnimation();
	useParallaxRight();
	useAppEffects();
	// Page transitions are handled by PageTransitionProvider
	useEffect(() => {
		// Scroll update all pages

		document.addEventListener('DOMContentLoaded', function () {
			function ScrollUpdateDelay() {
				setTimeout(function () {
					lenisRef.current?.resize();
				}, 500);
			}

			ScrollUpdateDelay();
		});
	});

	return (
		<ShowSettingProvider
			getMenu={getMenuData}
			getSettings={getSettingsData}
			getScrollData={lenisRef.current}
			scrollInstance={lenisRef.current}>
			<div onMouseEnter={CURSOR_SHOW} onMouseLeave={CURSOR_HIDE}>
				<StyledComponentsRegistry>
					<ThemeProvider theme={theme}>
						<ErrorBoundary>
							<HasprCursor>
								<GlobalStyle />
								{isInitialLoadRef.current && (
									<Preloader
										onComplete={() => {
											isInitialLoadRef.current = false;
											// Re-enable body scroll after preloader completes
											if (typeof document !== 'undefined') {
												document.body.style.overflow = '';
												document.documentElement.style.overflow =
													'';
											}
											// Refresh ScrollTrigger and other animations after a short delay
											setTimeout(() => {
												ScrollTrigger.refresh();
												// Also refresh Lenis if available
												if (lenisRef.current) {
													lenisRef.current.resize();
												}
											}, 200);
										}}
									/>
								)}
								<MenuComponent />
								<Toaster
									duration={5000}
									position={'bottom-right'}
									className={'Toastify__toast-container'}
									richColors
								/>
								<StickyButton
										link={getSettingsData?.office_whatsapp}
										classCustom="button-sticky-one"
										svg={
											'<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
											'<circle cx="30" cy="30" r="30" fill="#075E54"/>\n' +
											'<circle class="hover_stik" cx="30" cy="30" r="0" fill="#28282B"/>\n' +
											'<path d="M38.8125 21.1375C37.6665 19.9799 36.3015 19.0621 34.797 18.4376C33.2926 17.8131 31.6789 17.4943 30.05 17.5C23.225 17.5 17.6625 23.0625 17.6625 29.8875C17.6625 32.075 18.2375 34.2 19.3125 36.075L17.5625 42.5L24.125 40.775C25.9375 41.7625 27.975 42.2875 30.05 42.2875C36.875 42.2875 42.4375 36.725 42.4375 29.9C42.4375 26.5875 41.15 23.475 38.8125 21.1375ZM30.05 40.1875C28.2 40.1875 26.3875 39.6875 24.8 38.75L24.425 38.525L20.525 39.55L21.5625 35.75L21.3125 35.3625C20.2847 33.7212 19.7389 31.8241 19.7375 29.8875C19.7375 24.2125 24.3625 19.5875 30.0375 19.5875C32.7875 19.5875 35.375 20.6625 37.3125 22.6125C38.2719 23.5675 39.0321 24.7033 39.5492 25.9543C40.0664 27.2052 40.33 28.5464 40.325 29.9C40.35 35.575 35.725 40.1875 30.05 40.1875ZM35.7 32.4875C35.3875 32.3375 33.8625 31.5875 33.5875 31.475C33.3 31.375 33.1 31.325 32.8875 31.625C32.675 31.9375 32.0875 32.6375 31.9125 32.8375C31.7375 33.05 31.55 33.075 31.2375 32.9125C30.925 32.7625 29.925 32.425 28.75 31.375C27.825 30.55 27.2125 29.5375 27.025 29.225C26.85 28.9125 27 28.75 27.1625 28.5875C27.3 28.45 27.475 28.225 27.625 28.05C27.775 27.875 27.8375 27.7375 27.9375 27.5375C28.0375 27.325 27.9875 27.15 27.9125 27C27.8375 26.85 27.2125 25.325 26.9625 24.7C26.7125 24.1 26.45 24.175 26.2625 24.1625H25.6625C25.45 24.1625 25.125 24.2375 24.8375 24.55C24.5625 24.8625 23.7625 25.6125 23.7625 27.1375C23.7625 28.6625 24.875 30.1375 25.025 30.3375C25.175 30.55 27.2125 33.675 30.3125 35.0125C31.05 35.3375 31.625 35.525 32.075 35.6625C32.8125 35.9 33.4875 35.8625 34.025 35.7875C34.625 35.7 35.8625 35.0375 36.1125 34.3125C36.375 33.5875 36.375 32.975 36.2875 32.8375C36.2 32.7 36.0125 32.6375 35.7 32.4875Z" fill="white"/>\n' +
											'</svg>\n'
										}
								/>
								{/* Swup container for page transitions */}
								<div id="swup" className="transition-fade">
									{children}
								</div>
								<Footer scroll={lenisRef.current} />
								<CookieConsent />
							</HasprCursor>
						</ErrorBoundary>
					</ThemeProvider>
				</StyledComponentsRegistry>
			</div>
		</ShowSettingProvider>
	);
}
